import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageSquare, Calendar, ChevronDown, ChevronUp, Loader2, Share2, Users, Rss, Heart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import axios from 'axios';
import { toast } from 'sonner';
import ShareReflectionModal from '@/components/ShareReflectionModal';
import ShareToFriendModal from '@/components/ShareToFriendModal';
import PostToFeedModal from '@/components/PostToFeedModal';
import ScripturePopup from '@/components/ScripturePopup';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import SmartInstallBanner from '@/components/SmartInstallBanner';
import { useSignInGate } from '@/hooks/useSignInGate';

import { useSafeBack } from '@/utils/useSafeBack';
// ══════════════════════════════════════════════════════════════
// Theme category chip — visual tag showing the reflection angle
// ══════════════════════════════════════════════════════════════
const CATEGORY_META = {
  virtue:         { label: 'Character',     color: '#a78bfa' }, // violet
  inner_life:     { label: 'Heart',         color: '#60a5fa' }, // blue
  relational:     { label: 'Relationships', color: '#f472b6' }, // pink
  narrative:      { label: 'History',       color: '#fb923c' }, // orange
  figure:         { label: 'People',        color: '#facc15' }, // amber
  wisdom:         { label: 'Wisdom',        color: '#34d399' }, // emerald
  prophetic:      { label: 'Justice',       color: '#f87171' }, // red
  doctrine_lived: { label: 'Foundations',   color: '#22d3ee' }, // cyan
  ethics_modern:  { label: 'Stewardship',   color: '#a3e635' }, // lime
  tension:        { label: 'Tension',       color: '#c084fc' }, // purple
};

function CategoryChip({ category, size = 'sm', className = '' }) {
  const meta = CATEGORY_META[category];
  if (!meta) return null;
  const dims = size === 'xs'
    ? { padY: '1px', padX: '6px', font: '9px', dot: '5px' }
    : { padY: '2px', padX: '8px', font: '10px', dot: '6px' };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium uppercase tracking-wide ${className}`}
      style={{
        background: `${meta.color}1F`,        // ~12% alpha background
        color: meta.color,
        border: `1px solid ${meta.color}40`,   // ~25% alpha border
        padding: `${dims.padY} ${dims.padX}`,
        fontSize: dims.font,
        letterSpacing: '0.06em',
      }}
      data-testid={`category-chip-${category}`}
    >
      <span
        className="rounded-full"
        style={{ width: dims.dot, height: dims.dot, background: meta.color }}
      />
      {meta.label}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════
// Reflection Content — displays a single reflection
// ══════════════════════════════════════════════════════════════
function ReflectionContent({ reflection, onDiscuss, discussing, onShare, onShareToFriend, onPostToFeed, isFavorited, onToggleFavorite, onScriptureRefClick }) {
  const { t, i18n } = useTranslation();
  if (!reflection) return null;

  const dateLocale = i18n.language === 'es' ? 'es-ES' : i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'pt' ? 'pt-BR' : 'en-US';

  return (
    <article className="space-y-6" data-testid="reflection-content">
      {/* Topic Header with Favorite Heart */}
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <p className="text-xs uppercase tracking-widest font-medium" style={{ color: 'var(--theme-accent)' }}>
            {new Date(reflection.date + 'T12:00:00').toLocaleDateString(dateLocale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          {reflection.theme_category && <CategoryChip category={reflection.theme_category} />}
        </div>
        <div className="flex items-start gap-3">
          <h1
            className="flex-1 text-2xl md:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
            data-testid="reflection-topic"
          >
            {reflection.topic}
          </h1>
          <button
            onClick={() => onToggleFavorite(reflection.reflection_id)}
            className="mt-1 p-2 rounded-full transition-all hover:scale-110 active:scale-95 shrink-0"
            style={{ color: isFavorited ? '#ef4444' : 'var(--theme-textSecondary)' }}
            data-testid="favorite-reflection-btn"
          >
            <Heart className="h-5 w-5" fill={isFavorited ? '#ef4444' : 'none'} />
          </button>
        </div>
      </div>

      {/* Introduction */}
      <p
        className="text-base leading-relaxed"
        style={{ color: 'var(--theme-textSecondary)' }}
        data-testid="reflection-introduction"
        dangerouslySetInnerHTML={{
          __html: reflection.introduction?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') || ''
        }}
      />

      {/* Divider */}
      <div className="border-t" style={{ borderColor: 'var(--theme-border)' }} />

      {/* Body */}
      <div
        className="text-base leading-relaxed whitespace-pre-line reflection-body"
        style={{ color: 'var(--theme-text)' }}
        data-testid="reflection-body"
        dangerouslySetInnerHTML={{
          __html: reflection.body
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br/>')
        }}
      />

      {/* Thought to Consider */}
      <div
        className="rounded-xl p-5 md:p-6"
        style={{ background: 'var(--theme-accentLight)', border: `1px solid var(--theme-border)` }}
        data-testid="reflection-thought"
      >
        <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: 'var(--theme-accent)' }}>
          {t('reflection.something_to_consider')}
        </p>
        <p
          className="text-base leading-relaxed italic"
          style={{ color: 'var(--theme-text)' }}
          dangerouslySetInnerHTML={{
            __html: reflection.thought_to_consider?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') || ''
          }}
        />
      </div>

      {/* Family variant — 3-4 discussion questions */}
      {reflection.audience === 'family' && Array.isArray(reflection.discussion_questions) && reflection.discussion_questions.length > 0 && (
        <div
          className="rounded-xl p-5 md:p-6"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
          data-testid="reflection-family-questions"
        >
          <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: 'var(--theme-accent)' }}>
            Family Discussion
          </p>
          <ol className="space-y-2.5 pl-5 list-decimal marker:text-[var(--theme-accent)] marker:font-semibold">
            {reflection.discussion_questions.map((q, i) => (
              <li key={i} className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--theme-text)' }} data-testid={`family-question-${i}`}>
                {q}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Teen variant — real-life challenge */}
      {reflection.audience === 'teen' && reflection.real_life_challenge && (
        <div
          className="rounded-xl p-5 md:p-6"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
          data-testid="reflection-teen-challenge"
        >
          <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: 'var(--theme-accent)' }}>
            Real Life
          </p>
          <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--theme-text)' }}>
            {reflection.real_life_challenge}
          </p>
        </div>
      )}

      {/* Kids variant — hands-on activity */}
      {reflection.audience === 'kids' && reflection.kids_activity && (
        <div
          className="rounded-xl p-5 md:p-6"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
          data-testid="reflection-kids-activity"
        >
          <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: 'var(--theme-accent)' }}>
            Try This Together
          </p>
          <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--theme-text)' }}>
            {reflection.kids_activity}
          </p>
        </div>
      )}

      {/* Scripture References — tappable */}
      {reflection.scripture_references?.length > 0 && (
        <div data-testid="reflection-scripture-refs">
          <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: 'var(--theme-accent)' }}>
            {t('reflection.scripture_references')}
          </p>
          <div className="flex flex-wrap gap-2">
            {reflection.scripture_references.map((ref, i) => (
              <button
                key={i}
                onClick={() => onScriptureRefClick(ref)}
                className="text-sm px-3 py-1.5 rounded-lg transition-all hover:scale-105 active:scale-95 cursor-pointer"
                style={{
                  background: 'var(--theme-hover)',
                  color: 'var(--theme-accent)',
                  border: '1px solid var(--theme-border)',
                }}
                data-testid={`scripture-ref-${i}`}
              >
                {ref}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="pt-2 flex flex-wrap gap-3">
        <Button
          onClick={() => onDiscuss(reflection.reflection_id)}
          disabled={discussing}
          className="flex-1 min-w-[140px] rounded-xl px-6 py-3 text-sm font-medium transition-all"
          style={{ background: 'var(--theme-accent)', color: '#fff' }}
          data-testid="discuss-with-eili-btn"
        >
          {discussing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('reflection.opening_conversation')}</>
          ) : (
            <><MessageSquare className="h-4 w-4 mr-2" />{t('reflection.discuss_with_eili')}</>
          )}
        </Button>
        {onShare && (
        <Button
          onClick={() => onShare(reflection)}
          variant="outline"
          className="rounded-xl px-5 py-3 text-sm font-medium transition-all"
          style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-text)' }}
          data-testid="share-reflection-btn"
        >
          <Share2 className="h-4 w-4 mr-2" />{t('reflection.share')}
        </Button>
        )}
        {onShareToFriend && (
        <Button
          onClick={() => onShareToFriend(reflection)}
          variant="ghost" size="sm"
          className="rounded-xl px-4 py-3 text-sm font-medium transition-all hover:bg-[var(--theme-hover)]"
          style={{ color: 'var(--theme-text)' }}
          data-testid="share-reflection-friend-btn"
        >
          <Users className="h-4 w-4 mr-2" />{t('chat_messages.share_friend_tooltip', 'Share with Friend')}
        </Button>
        )}
        {onPostToFeed && (
        <Button
          onClick={() => onPostToFeed(reflection)}
          variant="ghost" size="sm"
          className="rounded-xl px-4 py-3 text-sm font-medium transition-all hover:bg-[var(--theme-hover)]"
          style={{ color: 'var(--theme-text)' }}
          data-testid="post-reflection-feed-btn"
        >
          <Rss className="h-4 w-4 mr-2" />{t('chat_messages.post_feed_tooltip', 'Post to Feed')}
        </Button>
        )}
      </div>
    </article>
  );
}

// ══════════════════════════════════════════════════════════════
// Main Page Component
// ══════════════════════════════════════════════════════════════
// Session-wide cache of today's reflection per audience (survives remounts).
const todayCache = new Map();
const TODAY_TTL_MS = 10 * 60 * 1000;
const fetchTodayCached = (audience) => {
  const hit = todayCache.get(audience);
  if (hit && Date.now() - hit.at < TODAY_TTL_MS) return hit.promise;
  const promise = axios.get(`${API}/reflections/today`, { params: { audience }, withCredentials: true })
    .then((res) => {
      todayCache.set(audience, { ...todayCache.get(audience), data: res.data });
      return res.data;
    });
  todayCache.set(audience, { at: Date.now(), promise, data: hit?.data });
  promise.catch(() => {
    const cur = todayCache.get(audience);
    if (cur?.promise === promise) todayCache.set(audience, { ...cur, at: 0 });
  });
  return promise;
};


export default function DailyReflectionPage({ user }) {
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'pt' ? 'pt-BR' : 'en-US';
  const [todayReflection, setTodayReflection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discussing, setDiscussing] = useState(false);
  const [shareToFriendData, setShareToFriendData] = useState(null);
  const [postToFeedData, setPostToFeedData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [shareReflection, setShareReflection] = useState(null);
  // Feature-flag gates for the action buttons (Share / Share with Friend / Post to Feed).
  const { enabled: reflectionShareEnabled } = useFeatureFlag('reflection_share');
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');
  // Favorites
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  // Scripture popup
  const [scripturePopup, setScripturePopup] = useState(null);
  // Guests cannot save/favorite — show a sign-in prompt instead.
  const { requireSignIn, signInModal } = useSignInGate(user);
  // Audience variant selection — persisted per browser so the user
  // returns to their preferred variant on the next visit.
  const { enabled: familyEnabled, loaded: flagsLoaded } = useFeatureFlag('family_reflections');
  const { enabled: teenEnabled } = useFeatureFlag('teen_reflections');
  const { enabled: kidsEnabled } = useFeatureFlag('kids_reflections');
  const availableAudiences = React.useMemo(() => {
    const list = [{ id: 'adult', label: 'Main' }];
    if (familyEnabled) list.push({ id: 'family', label: 'Family' });
    if (teenEnabled) list.push({ id: 'teen', label: 'Teens' });
    if (kidsEnabled) list.push({ id: 'kids', label: 'Kids' });
    return list;
  }, [familyEnabled, teenEnabled, kidsEnabled]);
  const [audience, setAudience] = useState(() => {
    try {
      const saved = localStorage.getItem('eili_reflection_audience');
      if (saved && ['adult', 'family', 'teen', 'kids'].includes(saved)) return saved;
    } catch { /* noop */ }
    return 'adult';
  });
  useEffect(() => {
    try { localStorage.setItem('eili_reflection_audience', audience); } catch { /* noop */ }
  }, [audience]);
  // If a previously-selected variant is turned off, fall back to Main.
  useEffect(() => {
    if (flagsLoaded && !availableAudiences.some((a) => a.id === audience)) {
      setAudience('adult');
    }
  }, [flagsLoaded, availableAudiences, audience]);

  // Prefetch every enabled variant in parallel so tab switches are instant.
  useEffect(() => {
    availableAudiences.forEach((a) => { fetchTodayCached(a.id).catch(() => {}); });
  }, [availableAudiences]);

  useEffect(() => {
    let cancelled = false;
    const apply = (data) => { setTodayReflection(data); setSelectedReflection(data); };
    const cached = todayCache.get(audience)?.data;
    if (cached) { apply(cached); setLoading(false); } else setLoading(true);
    fetchTodayCached(audience)
      .then((data) => { if (!cancelled && data !== cached) apply(data); })
      .catch(() => { if (!cancelled && !cached) toast.error(t('reflection.failed_load')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  useEffect(() => {
    axios.get(`${API}/reflections/favorites/ids`, { withCredentials: true })
      .then(res => setFavoriteIds(new Set(res.data?.ids || [])))
      .catch(() => {});
  }, []);

  const fetchHistory = async (page = 1, search = historySearch, cats = selectedCategories) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10, audience });
      if (search.trim()) params.set('search', search.trim());
      if (cats.length > 0) params.set('categories', cats.join(','));
      const res = await axios.get(`${API}/reflections/history?${params}`, { withCredentials: true });
      setHistory(res.data.items || []);
      setHistoryTotal(res.data.total || 0);
      setHistoryPage(page);
    } catch (err) {
      toast.error(t('reflection.failed_history', 'Failed to load history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  // Live search / filter with debounce — re-runs when audience changes too.
  useEffect(() => {
    if (!showHistory) return;
    const timer = setTimeout(() => {
      fetchHistory(1, historySearch, selectedCategories);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historySearch, selectedCategories, showHistory, audience]);

  const toggleCategoryFilter = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleToggleHistory = () => {
    if (!showHistory && history.length === 0) fetchHistory(1);
    setShowHistory(!showHistory);
    setShowFavorites(false);
  };

  const handleSelectHistoryItem = (item) => {
    setSelectedReflection(item);
    setShowHistory(false);
    setShowFavorites(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToToday = () => {
    if (todayReflection) {
      setSelectedReflection(todayReflection);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDiscuss = async (reflectionId) => {
    setDiscussing(true);
    try {
      const res = await axios.post(`${API}/reflections/${reflectionId}/discuss`, {}, { withCredentials: true });
      navigate(`/chat?conversation=${res.data.conversation_id}`);
    } catch (err) {
      toast.error(t('reflection.failed_discuss'));
      setDiscussing(false);
    }
  };

  const reflectionToMessage = (reflection) => ({
    content: reflection.body || reflection.introduction,
    _question: reflection.topic,
    message_id: reflection.reflection_id,
  });

  const handleShareToFriend = (reflection) => {
    setShareToFriendData({ message: reflectionToMessage(reflection), title: `Daily Reflection: ${reflection.topic}` });
  };

  const handlePostToFeed = (reflection) => {
    setPostToFeedData({ message: reflectionToMessage(reflection), title: `Daily Reflection: ${reflection.topic}` });
  };

  const handleToggleFavorite = async (reflectionId) => {
    if (requireSignIn('favorite this reflection')) return;
    try {
      const res = await axios.post(`${API}/reflections/${reflectionId}/favorite`, {}, { withCredentials: true });
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.favorited) {
          next.add(reflectionId);
        } else {
          next.delete(reflectionId);
          // Remove from favorites list if visible
          setFavorites(f => f.filter(r => r.reflection_id !== reflectionId));
        }
        return next;
      });
      toast.success(res.data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch {
      toast.error(t('generated.dailyreflectionpage_failed_to_update_9c1a'));
    }
  };

  const handleShowFavorites = async () => {
    if (showFavorites) {
      setShowFavorites(false);
      return;
    }
    setShowFavorites(true);
    setShowHistory(false);
    setFavoritesLoading(true);
    try {
      const res = await axios.get(`${API}/reflections/favorites`, { withCredentials: true });
      setFavorites(res.data?.favorites || []);
    } catch {
      toast.error(t('generated.dailyreflectionpage_failed_to_load_75bb'));
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleScriptureRefClick = (ref) => {
    setScripturePopup(ref);
  };

  const handleReadChapter = (ref) => {
    setScripturePopup(null);
    // Parse "John 3:16" → { book: "John", chapter: "3", verse: "16" }
    // Parse "1 Corinthians 13:4-7" → { book: "1 Corinthians", chapter: "13", verse: "4" }
    const match = ref.match(/^(.+?)\s+(\d+)(?::(\d+)(?:\s*[-–]\s*\d+)?)?$/);
    if (match) {
      const targetReference = {
        book: match[1],
        chapter: match[2],
        verse: match[3] || '',
      };
      navigate('/bible', { state: { targetReference } });
    } else {
      navigate('/bible');
    }
  };

  const isViewingToday = selectedReflection?.date === todayReflection?.date;

  return (
    <div
      className="page-container"
      style={{ background: 'var(--theme-background)', color: 'var(--theme-text)' }}
      data-testid="daily-reflection-page"
    >
      {/* Header */}
      <header
        className="px-5 md:px-8 py-4 flex items-center justify-between border-b sticky top-0 z-50"
        style={{
          borderColor: 'var(--theme-border)',
          background: 'var(--theme-surface)',
          // Status-bar safe-area inset (Despia injects on iOS, our IIFE
          // seeds 24px on Android Despia; web is 0).
          paddingTop: '1rem',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={goBackSafe}
            className="p-2 -ml-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
            data-testid="reflection-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-base font-semibold">{t('reflection.page_title', 'Daily Reflection')}</h2>
          </div>
        </div>
        <div className="flex items-center gap-1 relative">
          {!isViewingToday && todayReflection && (
            <Button
              onClick={handleBackToToday}
              variant="ghost"
              size="sm"
              className="rounded-full text-xs"
              style={{ color: 'var(--theme-accent)' }}
              data-testid="back-to-today-btn"
            >
              {t('reflection.today')}
            </Button>
          )}
          <Button
            onClick={handleToggleHistory}
            variant="ghost"
            size="sm"
            className="rounded-full"
            data-testid="reflection-history-toggle"
          >
            <Calendar className="h-4 w-4 mr-1" />
            <span className="text-xs">{t('reflection.past_reflections', 'History')}</span>
            {showHistory ? <ChevronUp className="h-3.5 w-3.5 ml-0.5" /> : <ChevronDown className="h-3.5 w-3.5 ml-0.5" />}
          </Button>
        </div>
      </header>
      {/* Audience selector — appears only if any family variant is enabled */}
      {availableAudiences.length > 1 && (
        <div
          className="mx-auto max-w-3xl px-4 md:px-6 pt-4 flex flex-col items-center text-center"
          data-testid="audience-selector"
        >
          <div
            role="tablist"
            aria-label="Reflection audience"
            className="inline-flex items-center gap-1 rounded-full p-1"
            style={{ background: 'var(--theme-hover)', border: '1px solid var(--theme-border)' }}
          >
            {availableAudiences.map((a) => {
              const active = audience === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setAudience(a.id)}
                  className="text-xs md:text-sm font-medium rounded-full px-3.5 py-1.5 transition-all"
                  style={{
                    background: active ? 'var(--theme-accent)' : 'transparent',
                    color: active ? 'var(--theme-background)' : 'var(--theme-textSecondary)',
                  }}
                  data-testid={`audience-tab-${a.id}`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
          {audience !== 'adult' && (
            <p className="mt-2 text-[11px] max-w-md" style={{ color: 'var(--theme-textSecondary)' }}>
              {audience === 'family' && 'A reflection the whole family can enjoy together — easy to read aloud, with a few questions to spark real conversation.'}
              {audience === 'teen' && 'Written for ages 13-18 — meeting teens where they are and tying today\'s Scripture to what they\'re actually walking through this week.'}
              {audience === 'kids' && 'For ages 5-10 — a short story a parent can read to their child, plus a quick 5-minute activity to help today\'s lesson stick.'}
            </p>
          )}
        </div>
      )}
      {/* History Dropdown Overlay — floats over content */}
      {showHistory && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowHistory(false)} />
          <div
            className="fixed right-4 z-50 w-[calc(100%-2rem)] max-w-sm rounded-2xl p-4 shadow-2xl overflow-y-auto"
            style={{
              background: 'var(--theme-surface)',
              border: '1px solid var(--theme-border)',
              top: '4rem',
              maxHeight: 'min(70vh, calc(var(--visual-vh, 100vh) - 96px))',
            }}
            data-testid="history-dropdown"
          >
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('reflection.past_reflections', 'Past Reflections')}
            </h3>
            {/* Live Search */}
            <div className="relative mb-3">
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder={t('generated.dailyreflectionpage_search_by_topic_77b1')}
                className="w-full text-sm rounded-lg px-3 py-2 pr-8 outline-none"
                style={{ background: 'var(--theme-hover)', color: 'var(--theme-text)', border: '1px solid var(--theme-border)' }}
                data-testid="history-search-input"
                autoFocus
              />
              {historySearch && (
                <button
                  onClick={() => setHistorySearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-[var(--theme-border)]"
                  data-testid="history-search-clear"
                >
                  <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
                </button>
              )}
            </div>
            {/* Category filter pills */}
            <div
              className="flex gap-1.5 overflow-x-auto pb-2 mb-1 -mx-1 px-1 scrollbar-none"
              style={{ scrollbarWidth: 'none' }}
              data-testid="history-category-filter"
            >
              {selectedCategories.length > 0 && (
                <button
                  onClick={() => setSelectedCategories([])}
                  className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide transition-all"
                  style={{
                    background: 'var(--theme-hover)',
                    color: 'var(--theme-textSecondary)',
                    border: '1px solid var(--theme-border)',
                    letterSpacing: '0.06em',
                  }}
                  data-testid="history-filter-clear"
                >
                  {t('generated.dailyreflectionpage_clear_f3cd')}
                </button>
              )}
              {Object.keys(CATEGORY_META).map((cat) => {
                const meta = CATEGORY_META[cat];
                const active = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategoryFilter(cat)}
                    className="shrink-0 inline-flex items-center gap-1 rounded-full font-medium uppercase tracking-wide transition-all"
                    style={{
                      background: active ? `${meta.color}33` : 'transparent',
                      color: active ? meta.color : 'var(--theme-textSecondary)',
                      border: `1px solid ${active ? `${meta.color}80` : 'var(--theme-border)'}`,
                      padding: '3px 8px',
                      fontSize: '10px',
                      letterSpacing: '0.06em',
                    }}
                    data-testid={`history-filter-${cat}`}
                    aria-pressed={active}
                  >
                    <span
                      className="rounded-full"
                      style={{ width: '6px', height: '6px', background: meta.color }}
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
            {historyLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} /></div>
            ) : history.length === 0 ? (
              <p className="text-sm text-center py-4" style={{ color: 'var(--theme-textSecondary)' }}>
                {historySearch
                  ? `No reflections match "${historySearch}"${selectedCategories.length ? ' with the selected filters' : ''}.`
                  : selectedCategories.length
                    ? 'No reflections match the selected categories.'
                    : 'No past reflections available.'}
              </p>
            ) : (
              <div className="space-y-1.5">
                {history.map((item) => {
                  const snippet = item.thought_to_consider || item.introduction || '';
                  const refs = (item.scripture_references || []).slice(0, 3).join(', ');
                  return (
                    <button
                      key={item.reflection_id}
                      onClick={() => handleSelectHistoryItem(item)}
                      className="w-full text-left p-3 rounded-xl transition-all hover:bg-[var(--theme-hover)]"
                      style={{
                        background: item.date === selectedReflection?.date ? 'var(--theme-hover)' : 'transparent',
                      }}
                      data-testid={`history-item-${item.date}`}
                    >
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <p className="text-[11px] font-medium" style={{ color: 'var(--theme-accent)' }}>
                          {new Date(item.date + 'T12:00:00').toLocaleDateString(dateLocale, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {item.theme_category && <CategoryChip category={item.theme_category} size="xs" />}
                          {favoriteIds.has(item.reflection_id) && (
                            <Heart className="h-3 w-3 shrink-0" fill="#ef4444" style={{ color: '#ef4444' }} />
                          )}
                        </div>
                      </div>
                      <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--theme-text)' }}>{item.topic}</p>
                      {snippet && (
                        <p className="text-[11px] mt-1 leading-snug line-clamp-2" style={{ color: 'var(--theme-textSecondary)' }}>
                          {snippet.length > 100 ? snippet.slice(0, 100) + '...' : snippet}
                        </p>
                      )}
                      {refs && (
                        <p className="text-[10px] mt-1" style={{ color: 'var(--theme-accent)', opacity: 0.7 }}>
                          {refs}
                        </p>
                      )}
                    </button>
                  );
                })}
                {historyTotal > history.length && (
                  <div className="flex justify-center gap-2 pt-2">
                    {historyPage > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => fetchHistory(historyPage - 1)} className="text-xs">
                        {t('common.previous', 'Previous')}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => fetchHistory(historyPage + 1)} className="text-xs">
                      {t('common.next', 'Next')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      <main className="page-content px-5 md:px-8 pt-8 pb-24 max-w-2xl lg:max-w-4xl mx-auto w-full">
        {/* Occasional install nudge — respects cooldown + session cap
            so it only surfaces every now and again for mobile-web
            visitors, never inside the Despia native app. */}
        <SmartInstallBanner source="reflection-banner" className="mb-6" />

        {/* Main Reflection Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('reflection.loading')}</p>
          </div>
        ) : selectedReflection ? (
          <ReflectionContent
            reflection={selectedReflection}
            onDiscuss={handleDiscuss}
            discussing={discussing}
            onShare={reflectionShareEnabled ? (r) => setShareReflection(r) : null}
            onShareToFriend={communityShareEnabled ? handleShareToFriend : null}
            onPostToFeed={communityShareEnabled ? handlePostToFeed : null}
            isFavorited={favoriteIds.has(selectedReflection.reflection_id)}
            onToggleFavorite={handleToggleFavorite}
            onScriptureRefClick={handleScriptureRefClick}
          />
        ) : (
          <div className="text-center py-20">
            <p className="text-lg font-medium" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('reflection.no_reflection')}
            </p>
          </div>
        )}

        {/* Favorites Section */}
        <div className="mt-10 border-t pt-6" style={{ borderColor: 'var(--theme-border)' }}>
          <button
            onClick={handleShowFavorites}
            className="flex items-center gap-2 mb-4 transition-all hover:opacity-80"
            data-testid="show-favorites-btn"
          >
            <Heart className="h-4 w-4" style={{ color: '#ef4444' }} fill="#ef4444" />
            <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.dailyreflectionpage_favorites_a9e9')}{favoriteIds.size})
                          </span>
            {showFavorites ? <ChevronUp className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} /> : <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />}
          </button>

          {showFavorites && (
            <div className="space-y-2">
              {favoritesLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} /></div>
              ) : favorites.length === 0 ? (
                <p className="text-sm py-4" style={{ color: 'var(--theme-textSecondary)' }}>
                  {t('generated.dailyreflectionpage_no_favorites_yet_ef65')}
                </p>
              ) : (
                favorites.map((item) => (
                  <button
                    key={item.reflection_id}
                    onClick={() => handleSelectHistoryItem(item)}
                    className="w-full text-left p-3 rounded-xl transition-all hover:bg-[var(--theme-hover)]"
                    style={{ background: item.date === selectedReflection?.date ? 'var(--theme-hover)' : 'transparent', border: '1px solid var(--theme-border)' }}
                    data-testid={`favorite-item-${item.date}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs" style={{ color: 'var(--theme-accent)' }}>
                        {new Date(item.date + 'T12:00:00').toLocaleDateString(dateLocale, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {item.theme_category && <CategoryChip category={item.theme_category} size="xs" />}
                        <Heart className="h-3 w-3" fill="#ef4444" style={{ color: '#ef4444' }} />
                      </div>
                    </div>
                    <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--theme-text)' }}>{item.topic}</p>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </main>
      {/* Scripture Popup */}
      {scripturePopup && (
        <ScripturePopup
          reference={scripturePopup}
          onClose={() => setScripturePopup(null)}
          onReadChapter={handleReadChapter}
        />
      )}
      {/* Share Modal */}
      {shareReflection && (
        <ShareReflectionModal reflection={shareReflection} onClose={() => setShareReflection(null)} />
      )}
      {shareToFriendData && (
        <ShareToFriendModal message={shareToFriendData.message} conversationTitle={shareToFriendData.title} onClose={() => setShareToFriendData(null)} />
      )}
      {postToFeedData && (
        <PostToFeedModal message={postToFeedData.message} conversationTitle={postToFeedData.title} onClose={() => setPostToFeedData(null)} />
      )}
      {signInModal}
    </div>
  );
}
